// ✅ src/pages/VehicleV2/SpareLifeDetailsReport.jsx  (FULL FRONTEND)
// ASPX: SpareLifeDetaisReport.aspx  (Spare Life Detail Report)
//
// ✅ UI/Style matches your sample template (VehicleSparesDetailsReport.jsx)
// ✅ Filters match ASPX controls:
//   - Month / Year (ddl_month, ddl_year)
//   - From / To date (txt_search_fromdate, txt_search_todate) with same validation rules
//   - Vehicle RegNo / Code / Name
//   - Vehicle Main Type (ddl_vehicletype)  ✅ dropdown (not autocomplete)
//   - Item Category (ddl_itemcategory)
//   - Spare (txt_itemname)  ✅ autocomplete
//   - Status (ddl_status)
//   - Branch (ddl_Search_Branch)
//
// ✅ Expected backend (you can keep same naming as your other pages):
//   app.use("/api/spareLifeDetailsReport", spareLifeDetailsReportRouter);
//
//   GET  /api/spareLifeDetailsReport/meta?branchId=8&branchType=HO&loginUserId=37
//        -> { success:true, branches:[{label,value}], itemCategories:[{label,value}], vehicleMainTypes:[{label,value}] }
//           vehicleMainTypes value can be your "fld_vehicletypeindex" OR an id—just be consistent.
//
//   GET  /api/spareLifeDetailsReport/list?...params...
//        -> { success:true, rows:[...], count:123 }
//
//   GET  /api/spareLifeDetailsReport/ac/itemName?branchId=8&prefix=fi
//        -> { success:true, items:[...] }  OR just an array
//
// Optional (if you want vehicle regno/code/name autocomplete like ASPX):
//   GET /api/spareLifeDetailsReport/ac/vehicleRegNo
//   GET /api/spareLifeDetailsReport/ac/vehicleCode
//   GET /api/spareLifeDetailsReport/ac/vehicleName

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/spareLifeDetailsReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const pick = (row, keys, def = "") => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return def;
};

const exportToCsv = (rows, filename = "spare-life-detail.csv") => {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const cell = r[h] ?? "";
          const s = String(cell).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const isFutureDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(yyyyMmDd);
  d.setHours(0, 0, 0, 0);
  return d > today;
};

const currentMonth = () => new Date().getMonth() + 1;
const currentYear = () => new Date().getFullYear();

/* ---------------- main ---------------- */
export default function SpareLifeDetailsReport() {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // storage

  
   const branchIdFromStorage = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";
  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // meta dropdowns
  const [meta, setMeta] = useState({
    branches: [],
    itemCategories: [],
    vehicleMainTypes: [], // ddl_vehicletype in ASPX
  });

  // locks (if you want to mimic Session["Sparevehicleid"] flow)
  const [locks, setLocks] = useState({
    lockVehicleCode: false,
    lockVehicleRegNo: false,
    lockBranch: false,
    lockVehicleName: false,
    lockVehicleMainType: false,
  });

  // filters (match ASPX)
  const [filters, setFilters] = useState({
    // ASPX has rbOwn/rbHire but hidden; keep default "O"
    vehtype: "O",

    month: 0,
    year: 0,

    fromDate: "",
    toDate: "",

    vehicleRegNo: "",
    vehicleCode: "",
    vehicleName: "",

    // ddl_vehicletype selected value (index or id)
    vehicleMainTypeValue: "Select", // keep "Select" like ASPX

    itemCategoryId: 0,
    itemName: "",

    status: "", // "", "Current", "Previous"
    branchId: branchIdFromStorage,
  });

  const [rows, setRows] = useState([]);

  // autocomplete
  const [ac, setAc] = useState({
    vehicleRegNo: [],
    vehicleCode: [],
    vehicleName: [],
    itemName: [],
  });

  // pagination (client-side)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  /* ---------------- querystring prefill (optional) ----------------
     Support same style as your sample (Sparevehiclecode, Sparevehicleregno, Sparebranchid, etc.)
  */
  const spareParams = useMemo(() => {
    const qs = new URLSearchParams(search);
    return {
      Sparevehicleid: toStr(qs.get("Sparevehicleid")),
      Sparevehiclecode: toStr(qs.get("Sparevehiclecode")),
      Sparevehicleregno: toStr(qs.get("Sparevehicleregno")),
      Sparebranchid: toStr(qs.get("Sparebranchid")),
      Sparevehiclebranchid: toStr(qs.get("Sparevehiclebranchid")),
      hasAny:
        !!qs.get("Sparevehicleid") ||
        !!qs.get("Sparevehiclecode") ||
        !!qs.get("Sparevehicleregno") ||
        !!qs.get("Sparebranchid") ||
        !!qs.get("Sparevehiclebranchid"),
    };
  }, [search]);

  const stripSpareParamsFromUrl = () => {
    const qs = new URLSearchParams(search);
    ["Sparevehicleid", "Sparevehiclecode", "Sparevehicleregno", "Sparebranchid", "Sparevehiclebranchid"].forEach((k) =>
      qs.delete(k)
    );
    const newQs = qs.toString();
    window.history.replaceState({}, "", newQs ? `${pathname}?${newQs}` : `${pathname}`);
  };

  /* ---------------- validation (same as ASPX DateValid) ---------------- */
  const validateDates = (f) => {
    if (f.fromDate && isFutureDate(f.fromDate)) {
      toast.error("Select from date lesser than current date");
      return false;
    }
    if (f.toDate && isFutureDate(f.toDate)) {
      toast.error("Select to date lesser than current date");
      return false;
    }
    if (f.fromDate && f.toDate) {
      const a = new Date(f.fromDate).getTime();
      const b = new Date(f.toDate).getTime();
      if (b < a) {
        toast.error("Please select from date less than to date");
        return false;
      }
    }
    return true;
  };

  /* ---------------- APIs ---------------- */
  const loadMeta = async () => {
    try {
      const rs = await axios.get(`${API_BASE}/meta`, {
        params: {
          branchId: branchIdFromStorage,
          branchType,
          loginUserId,
        },
      });
      if (!rs.data?.success) throw new Error(rs.data?.message || "Meta load failed");

      setMeta({
        branches: rs.data.branches || [],
        itemCategories: rs.data.itemCategories || [],
        vehicleMainTypes: rs.data.vehicleMainTypes || [],
      });
    } catch (e) {
      toast.error(e.message || "Failed to load dropdowns");
    }
  };

  const loadList = async (f = filters, showToast = true) => {
    try {
      // ASPX requires spare name
      if (!toStr(f.itemName)) {
        toast.error("please enter the Spare Name to search");
        setRows([]);
        return;
      }

      if (!validateDates(f)) {
        setRows([]);
        return;
      }

      setLoading(true);

      // ASPX: if month selected, they usually clear from/to
      const monthSelected = toNum(f.month, 0) > 0;
      const effective = {
        ...f,
        fromDate: monthSelected ? "" : toStr(f.fromDate),
        toDate: monthSelected ? "" : toStr(f.toDate),
      };

      const params = {
        branchId: toNum(effective.branchId, branchIdFromStorage),
        branchType,
        loginUserId,

        vehtype: toStr(effective.vehtype || "O"),

        month: toNum(effective.month, 0),
        year: toNum(effective.year, 0),

        fromDate: toStr(effective.fromDate),
        toDate: toStr(effective.toDate),

        vehicleRegNo: toStr(effective.vehicleRegNo),
        vehicleCode: toStr(effective.vehicleCode),
        vehicleName: toStr(effective.vehicleName),

        // value like "Select" or an index/id
        vehicleMainTypeValue: toStr(effective.vehicleMainTypeValue || "Select"),

        itemCategoryId: toNum(effective.itemCategoryId, 0),
        itemName: toStr(effective.itemName),

        status: toStr(effective.status), // "", "Current", "Previous"
      };

      const rs = await axios.get(`${API_BASE}/list`, { params });
      if (!rs.data?.success) throw new Error(rs.data?.message || "List load failed");

      const list = rs.data.rows || [];
      setRows(list);
      setCurrentPage(1);

      if (showToast) toast.success(`Found ${rs.data.count ?? list.length} records`);
    } catch (e) {
      toast.error(e.message || "Failed to load report");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- one-time init ---------------- */
  const appliedRef = useRef(false);

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (appliedRef.current) return;

    // If no spare params, do nothing (ASPX didn't auto-search)
    if (!spareParams.hasAny) {
      appliedRef.current = true;
      return;
    }

    appliedRef.current = true;

    const next = { ...filters };

    if (spareParams.Sparevehiclecode) next.vehicleCode = spareParams.Sparevehiclecode;
    if (spareParams.Sparevehicleregno) next.vehicleRegNo = spareParams.Sparevehicleregno;

    const b1 = spareParams.Sparebranchid;
    const b2 = spareParams.Sparevehiclebranchid;
    const bCandidate = !b1 || b1 === "Select" ? b2 : b1;
    const bNum = toNum(bCandidate, 0);
    next.branchId = bNum > 0 ? bNum : branchIdFromStorage;

    // mimic your sample: set current month/year and auto-search once (only if itemName exists)
    next.month = currentMonth();
    next.year = currentYear();

    setLocks({
      lockVehicleCode: !!next.vehicleCode,
      lockVehicleRegNo: !!next.vehicleRegNo,
      lockBranch: true,
      lockVehicleName: false,
      lockVehicleMainType: false,
    });

    setFilters(next);

    (async () => {
      // Only auto-search if spare is already passed/typed (optional)
      if (toStr(next.itemName)) {
        await loadList(next, false);
        setFilters((p) => ({ ...p, month: 0, year: 0 }));
      }
      stripSpareParamsFromUrl();
      toast.info("Vehicle details loaded from passed URL");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spareParams.hasAny]);

  /* ---------------- autocomplete (debounced) ---------------- */
  const debounceRef = useRef({});
  const debounce = (key, fn, delay = 250) => {
    clearTimeout(debounceRef.current[key]);
    debounceRef.current[key] = setTimeout(fn, delay);
  };

  const loadAC = (key, url, params) => {
    debounce(key, async () => {
      try {
        const rs = await axios.get(url, { params });
        const items = rs.data?.items || rs.data || [];
        setAc((p) => ({ ...p, [key]: Array.isArray(items) ? items : [] }));
      } catch {
        // ignore
      }
    });
  };

  // ItemName autocomplete (matches ASPX AutoCompleteExtender4: usp_vehicleSpare_itemsearch)
  useEffect(() => {
    const prefix = toStr(filters.itemName);
    if (prefix.length < 1) return setAc((p) => ({ ...p, itemName: [] }));
    loadAC("itemName", `${API_BASE}/ac/itemName`, {
      branchId: toNum(filters.branchId, branchIdFromStorage),
      prefix,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.itemName, filters.branchId]);

  // OPTIONAL: vehicle fields autocomplete like ASPX
  useEffect(() => {
    const prefix = toStr(filters.vehicleRegNo);
    if (prefix.length < 1) return setAc((p) => ({ ...p, vehicleRegNo: [] }));
    loadAC("vehicleRegNo", `${API_BASE}/ac/vehicleRegNo`, {
      branchId: toNum(filters.branchId, branchIdFromStorage),
      vehtype: filters.vehtype,
      prefix,
      vehicleName: toStr(filters.vehicleName),
      vehicleCode: toStr(filters.vehicleCode),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.vehicleRegNo, filters.branchId, filters.vehtype]);

  useEffect(() => {
    const prefix = toStr(filters.vehicleCode);
    if (prefix.length < 1) return setAc((p) => ({ ...p, vehicleCode: [] }));
    loadAC("vehicleCode", `${API_BASE}/ac/vehicleCode`, {
      branchId: toNum(filters.branchId, branchIdFromStorage),
      branchType,
      vehtype: filters.vehtype,
      prefix,
      vehicleRegNo: toStr(filters.vehicleRegNo),
      vehicleName: toStr(filters.vehicleName),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.vehicleCode, filters.branchId, filters.vehtype]);

  useEffect(() => {
    const prefix = toStr(filters.vehicleName);
    if (prefix.length < 1) return setAc((p) => ({ ...p, vehicleName: [] }));
    loadAC("vehicleName", `${API_BASE}/ac/vehicleName`, {
      branchId: toNum(filters.branchId, branchIdFromStorage),
      vehtype: filters.vehtype,
      prefix,
      vehicleCode: toStr(filters.vehicleCode),
      vehicleRegNo: toStr(filters.vehicleRegNo),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.vehicleName, filters.branchId, filters.vehtype]);

  /* ---------------- handlers ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (["month", "year", "itemCategoryId", "branchId"].includes(name)) {
      const n = toNum(value, 0);

      // ASPX: if month selected, clear dates
      if (name === "month" && n > 0) {
        setFilters((p) => ({ ...p, month: n, fromDate: "", toDate: "" }));
        return;
      }

      setFilters((p) => ({ ...p, [name]: n }));
      return;
    }

    setFilters((p) => ({ ...p, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters((p) => ({
      ...p,
      month: 0,
      year: 0,
      fromDate: "",
      toDate: "",
      vehicleRegNo: locks.lockVehicleRegNo ? p.vehicleRegNo : "",
      vehicleCode: locks.lockVehicleCode ? p.vehicleCode : "",
      vehicleName: "",
      vehicleMainTypeValue: "Select",
      itemCategoryId: 0,
      itemName: "",
      status: "",
      branchId: locks.lockBranch ? p.branchId : branchIdFromStorage,
    }));
    toast.info("Filters cleared");
  };

  const handleSearch = () => loadList(filters);

  const handleViewAll = () => {
    // ASPX doesn’t really have view all; here we just search with current filters.
    // You can also decide to clear month/year/dates and search.
    const next = {
      ...filters,
      month: 0,
      year: 0,
      fromDate: "",
      toDate: "",
      vehicleName: "",
      vehicleMainTypeValue: "Select",
      itemCategoryId: 0,
      status: "",
      branchId: locks.lockBranch ? filters.branchId : branchIdFromStorage,
    };
    setFilters(next);
    loadList(next);
  };

  const handleExport = () => {
    if (!rows.length) return toast.info("No rows to export");
    exportToCsv(rows, "spare-life-detail.csv");
    toast.success("Exported CSV");
  };

  const handlePrint = () => {
    if (!rows.length) {
      toast.info("No rows to print");
      return;
    }

    const qs = new URLSearchParams({
      vehtype: toStr(filters.vehtype || "O"),
      month: String(toNum(filters.month, 0)),
      year: String(toNum(filters.year, 0)),
      fromDate: toStr(filters.fromDate),
      toDate: toStr(filters.toDate),

      vehicleRegNo: toStr(filters.vehicleRegNo),
      vehicleCode: toStr(filters.vehicleCode),
      vehicleName: toStr(filters.vehicleName),
      vehicleMainTypeValue: toStr(filters.vehicleMainTypeValue || "Select"),

      itemCategoryId: String(toNum(filters.itemCategoryId, 0)),
      itemName: toStr(filters.itemName),

      status: toStr(filters.status),
      branchId: String(toNum(filters.branchId, branchIdFromStorage)),
      branchType: toStr(branchType),
      loginUserId: String(toNum(loginUserId, 0)),
    }).toString();

    // ✅ open print page like ASPX window.open
    window.open(`/vehicle/SpareLifeDetailsReport_Print?${qs}`, "_blank");
  };

  /* ---------------- pagination ---------------- */
  const totalPages = Math.ceil(rows.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentItems = useMemo(
    () => rows.slice(indexOfFirstItem, indexOfLastItem),
    [rows, indexOfFirstItem, indexOfLastItem]
  );

  const paginate = (p) => setCurrentPage(p);

  const months = useMemo(
    () => [
      { value: 0, label: "Select" },
      { value: 1, label: "January" },
      { value: 2, label: "February" },
      { value: 3, label: "March" },
      { value: 4, label: "April" },
      { value: 5, label: "May" },
      { value: 6, label: "June" },
      { value: 7, label: "July" },
      { value: 8, label: "August" },
      { value: 9, label: "September" },
      { value: 10, label: "October" },
      { value: 11, label: "November" },
      { value: 12, label: "December" },
    ],
    []
  );

  const years = useMemo(() => {
    const list = [{ value: 0, label: "Select" }];
    for (let y = 2006; y <= 2035; y++) list.push({ value: y, label: String(y) });
    return list;
  }, []);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SPARE LIFE DETAIL REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Spare Life Detail Report
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {rows.length} Records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                {/* Month */}
                <div>
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={inputClass}>
                    {months.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Year */}
                <div>
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={inputClass}>
                    {years.map((y) => (
                      <option key={y.value} value={y.value}>
                        {y.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* From */}
                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={toNum(filters.month, 0) > 0}
                  />
                </div>

                {/* To */}
                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={toNum(filters.month, 0) > 0}
                  />
                </div>

                {/* Vehicle RegNo */}
                <div>
                  <label className={labelClass}>Vehicle Registration Number</label>
                  <input
                    name="vehicleRegNo"
                    value={filters.vehicleRegNo}
                    onChange={handleChange}
                    className={inputClass}
                    list="ac_vehicleRegNo"
                    placeholder="Type to search..."
                    disabled={locks.lockVehicleRegNo}
                  />
                  <datalist id="ac_vehicleRegNo">
                    {ac.vehicleRegNo.map((x, i) => (
                      <option key={`${x}-${i}`} value={x} />
                    ))}
                  </datalist>
                </div>

                {/* Vehicle Code */}
                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={inputClass}
                    list="ac_vehicleCode"
                    placeholder="Type to search..."
                    disabled={locks.lockVehicleCode}
                  />
                  <datalist id="ac_vehicleCode">
                    {ac.vehicleCode.map((x, i) => (
                      <option key={`${x}-${i}`} value={x} />
                    ))}
                  </datalist>
                </div>

                {/* Vehicle Name */}
                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    className={inputClass}
                    list="ac_vehicleName"
                    placeholder="Type to search..."
                    disabled={locks.lockVehicleName}
                  />
                  <datalist id="ac_vehicleName">
                    {ac.vehicleName.map((x, i) => (
                      <option key={`${x}-${i}`} value={x} />
                    ))}
                  </datalist>
                </div>

                {/* Vehicle Main Type (ddl_vehicletype) */}
                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleMainTypeValue"
                    value={filters.vehicleMainTypeValue}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={locks.lockVehicleMainType}
                  >
                    <option value="Select">Select</option>
                    {meta.vehicleMainTypes.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Item Category */}
                <div>
                  <label className={labelClass}>Item Category</label>
                  <select
                    name="itemCategoryId"
                    value={filters.itemCategoryId}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value={0}>Select</option>
                    {meta.itemCategories.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Spare (REQUIRED in ASPX) */}
                <div>
                  <label className={labelClass}>
                    Spare <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="itemName"
                    value={filters.itemName}
                    onChange={handleChange}
                    className={inputClass}
                    list="ac_itemName"
                    placeholder="Type spare name..."
                  />
                  <datalist id="ac_itemName">
                    {ac.itemName.map((x, i) => (
                      <option key={`${x}-${i}`} value={x} />
                    ))}
                  </datalist>
                </div>

                {/* Status */}
                <div>
                  <label className={labelClass}>Status</label>
                  <select name="status" value={filters.status} onChange={handleChange} className={inputClass}>
                    <option value="">Select</option>
                    <option value="Current">Current</option>
                    <option value="Previous">Previous</option>
                  </select>
                </div>

                {/* Branch */}
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branchId"
                    value={filters.branchId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={locks.lockBranch || branchType !== "HO"}
                  >
                    <option value={filters.branchId || branchIdFromStorage}>Select</option>
                    {meta.branches.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium"
                >
                  Clear All Filters
                </button>

                <div className="flex gap-4 flex-wrap justify-end">
                  <button
                    onClick={handleViewAll}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold`}
                    disabled={loading}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search Records"}
                  </button>

                  <button
                    type="button"
                    onClick={handlePrint}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold`}
                  >
                    <FaPrint />
                    Print
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No Records Found
                </h3>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Issue Date</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Branch Name</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Uom Name</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Category Name</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Item Name</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Vehicle Code</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Vehicle RegNo</th>
                      <th className="px-4 py-4 text-right font-semibold whitespace-nowrap">Qty</th>
                      <th className="px-4 py-4 text-right font-semibold whitespace-nowrap">Price</th>
                      <th className="px-4 py-4 text-right font-semibold whitespace-nowrap">Total</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Fitting Date</th>
                      <th className="px-4 py-4 text-right font-semibold whitespace-nowrap">Fitting Hr/Km</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Removing Date</th>
                      <th className="px-4 py-4 text-right font-semibold whitespace-nowrap">Removing Hr/Km</th>
                      <th className="px-4 py-4 text-right font-semibold whitespace-nowrap">Life Hr/Km</th>
                      <th className="px-4 py-4 text-right font-semibold whitespace-nowrap">Cost Hr/Km</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Status</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {currentItems.map((r, idx) => {
                      const issueDate = pick(r, ["issuedate", "IssueDate"]);
                      const branchName = pick(r, ["fld_branchname", "BranchName"]);
                      const uomName = pick(r, ["fld_uomname", "UomName"]);
                      const catName = pick(r, ["fld_itemcategory_name", "CategoryName"]);
                      const itemName = pick(r, ["fld_itemname", "ItemName"]);
                      const vehCode = pick(r, ["fld_vehicle_code", "VehicleCode"]);
                      const regNo = pick(r, ["fld_vehicle_regno", "VehicleRegNo"]);
                      const qty = pick(r, ["fld_qty", "Qty"]);
                      const price = pick(r, ["fld_itemPrice", "Price"]);
                      const totalAmt = pick(r, ["total", "Total"]);
                      const fittingDate = pick(r, ["fld_progressentrydate", "FittingDate"]);
                      const fittingHrKm = pick(r, ["fittinghrkm", "FittingHrKm"]);
                      const removingDate = pick(r, ["fld_RemovingDate", "RemovingDate", "fld_Removingdate"]);
                      const removingHrKm = pick(r, ["removinghrkm", "RemovingHrKm"]);
                      const life = pick(r, ["life", "Life"]);
                      const cost = pick(r, ["cost", "Cost"]);
                      const status = pick(r, ["status", "Status"]);

                      return (
                        <tr
                          key={idx}
                          className={`${isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"}`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {indexOfFirstItem + idx + 1}
                          </td>
                          <td className={`px-4 py-4 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {issueDate || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {branchName || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {uomName || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {catName || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {itemName || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {vehCode || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {regNo || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {qty || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {price || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm text-right font-medium ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {totalAmt || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {fittingDate || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {fittingHrKm || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {removingDate || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {removingHrKm || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {life || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {cost || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {status || "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, rows.length)} of {rows.length} entries
              </div>

              <nav className="flex gap-1">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === pageNum
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}